import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PhoneMockup from './loksy-landing/PhoneMockup';
import { PlayStoreBadge, WebsiteLink } from './loksy-landing/AppLinks';
import { loksyVideos, productsForBrand } from './loksy-landing/videos';
import {
  getPublicSiteBrand,
  showKnlsLms,
  showLoksyApps,
} from '../../utils/publicSiteBrand';
import './loksy-landing/loksyLanding.css';

const OPERATE = ['Smart Billing', 'Leads', 'Attendance', 'Pending Work', 'Reports', 'Operator Lock'];
const GROW = ['Business Profile', 'Products', 'Services', 'Coupons', 'Promotions', 'Citizen Requirements'];
const CITIZEN_LABELS = [
  ['01', 'Discover Shops'],
  ['02', 'Find Local Jobs'],
  ['03', 'Buy & Sell'],
  ['04', 'Coupons & Deals'],
  ['05', 'QR / UPI Pay'],
];

export default function LoksyLanding() {
  const brand = getPublicSiteBrand();
  const loksyApps = showLoksyApps(brand);
  const lmsApp = showKnlsLms(brand);
  const knlsOnly = brand === 'knls';
  const visibleProducts = productsForBrand(brand);
  const brandName = knlsOnly ? 'KNLS' : 'Loksy';

  const nav = [
    { href: '#home', label: 'Home' },
    loksyApps ? { href: '/demo', label: 'Demo' } : null,
    loksyApps ? { href: '/loksyapp', label: 'Loksy App' } : null,
    loksyApps ? { href: '/loksymitra', label: 'Loksy Vyapar' } : null,
    lmsApp ? { href: '#lms', label: knlsOnly ? 'KNLS LMS' : 'LMS' } : null,
    knlsOnly ? { href: '/jobalerts', label: 'Job Alerts' } : null,
  ].filter(Boolean);

  const [menuOpen, setMenuOpen] = useState(false);
  const [product, setProduct] = useState(visibleProducts[0]);
  const [chapter, setChapter] = useState(loksyVideos[visibleProducts[0]].chapters[0]);
  const [zone, setZone] = useState('operate');
  const [citizenChapter, setCitizenChapter] = useState(loksyVideos.citizen.chapters[0]);
  const [lmsChapter, setLmsChapter] = useState(loksyVideos.lms.chapters[0]);
  const heroVideo = loksyVideos[product] || loksyVideos[visibleProducts[0]];
  const zoneItems = zone === 'operate' ? OPERATE : GROW;
  const downloadApps = visibleProducts.map((id) => loksyVideos[id]);
  const loopNodes = lmsApp
    ? [
        ['Vyapar Grow', 'Publish & Promote'],
        ['Citizen', 'Discover & Engage'],
        ['Vyapar Operate', 'Bill / Lead / Attend'],
        ['Student LMS', 'Learn & Grow'],
      ]
    : [
        ['Vyapar Grow', 'Publish & Promote'],
        ['Citizen', 'Discover & Engage'],
        ['Vyapar Operate', 'Bill / Lead / Attend'],
      ];
  const audience = [
    loksyApps ? ['Resident', 'Find shops, jobs, deals and local services.', 'Citizen', '/loksyapp'] : null,
    loksyApps ? ['Shop Owner', 'Run the counter and grow the business.', 'Vyapar', '/loksymitra'] : null,
    lmsApp ? ['Student', 'Learn, practice and track progress.', knlsOnly ? 'KNLS LMS' : 'Students LMS', '#lms'] : null,
    knlsOnly ? ['Job seeker', 'Browse latest government and private job alerts.', 'Job Alerts', '/jobalerts'] : null,
  ].filter(Boolean);

  useEffect(() => {
    document.body.classList.add('loksy-landing-active');
    const previous = document.title;
    document.title = knlsOnly
      ? 'KNLS LMS — Learn to Earn'
      : 'Loksy — Your Town. Just One Click Away.';
    return () => {
      document.body.classList.remove('loksy-landing-active');
      document.title = previous;
    };
  }, [knlsOnly]);

  return (
    <div className="loksy-landing">
      <header className="loksy-nav">
        <a href="#home" className="loksy-brand">
          {brandName}
          <span />
        </a>
        <ul className="loksy-nav-links">
          {nav.map((item) => (
            <li key={item.href}>
              <a href={item.href}>{item.label}</a>
            </li>
          ))}
        </ul>
        <div className="loksy-nav-actions">
          <a href="#download" className="loksy-btn loksy-btn-primary">
            {knlsOnly ? 'Get the App' : 'Get the Apps'}
          </a>
          <button
            type="button"
            className="loksy-menu"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <i />
            <b />
          </button>
        </div>
      </header>
      <div className={`loksy-mobile-nav ${menuOpen ? 'open' : ''}`}>
        {nav.map((item) => (
          <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
            {item.label}
          </a>
        ))}
      </div>

      <main>
        <section id="home" className="loksy-hero">
          <div className="loksy-hero-grid">
            <div className="loksy-block-heading">
              <p className="loksy-kicker">{knlsOnly ? 'KNLS LMS' : 'Loksy'}</p>
              {knlsOnly ? (
                <h1 className="loksy-h1">
                  Learn to Earn
                  <br />
                  (To Build your Career)
                </h1>
              ) : (
                <h1 className="loksy-h1">
                  Your Town.
                  <br />
                  Just One Click Away.
                </h1>
              )}
              {knlsOnly ? (
                <p className="loksy-telugu-line">Courses, reading, flashcards and typing — on the phone.</p>
              ) : (
                <p className="loksy-telugu-line">మీ ఊరు, ఒక్క క్లిక్‌లో.</p>
              )}
            </div>
            <div className="loksy-block-copy">
              <p className="loksy-lede">
                {knlsOnly
                  ? 'Courses, track sheets, quizzes and learning progress in one student app.'
                  : loksyApps && !lmsApp
                    ? 'Discover local shops, run your business, and find opportunities — all through Loksy.'
                    : 'Discover local shops, run your business, find opportunities, and learn — all through Loksy.'}
              </p>
              <div className="loksy-chapter-note loksy-chapter-quote" key={`${product}-${chapter.title}`}>
                <p className="loksy-kicker">{chapter.title}</p>
                <p>{chapter.description}</p>
              </div>
            </div>
            <div className="loksy-block-links loksy-cta-row">
              {loksyApps ? (
                <>
                  <Link to="/loksyapp" className="loksy-btn loksy-btn-primary">
                    Loksy App
                  </Link>
                  <Link to="/loksymitra" className="loksy-btn loksy-btn-ghost">
                    Loksy Vyapar
                  </Link>
                </>
              ) : (
                <>
                  <PlayStoreBadge href={heroVideo.playStore} name={heroVideo.title} />
                  <WebsiteLink to={heroVideo.webTo}>{heroVideo.webLabel}</WebsiteLink>
                </>
              )}
            </div>
            {knlsOnly ? (
              <div className="loksy-block-links" style={{ marginTop: 12 }}>
                <Link to="/jobalerts" className="loksy-btn loksy-btn-ghost">
                  Job Alerts
                </Link>
              </div>
            ) : null}
            <div className="loksy-phone-col">
              <PhoneMockup
                key={heroVideo.id}
                video={heroVideo}
                size="hero"
                glow={knlsOnly ? 'indigo' : 'orange'}
                lazy={false}
                enter
                onChapterChange={setChapter}
                onEnded={() => {
                  const index = visibleProducts.indexOf(product);
                  const next = visibleProducts[(index + 1) % visibleProducts.length];
                  setProduct(next);
                  setChapter(loksyVideos[next].chapters[0]);
                }}
              />
              {visibleProducts.length > 1 ? (
                <div className="loksy-selector">
                  <p>What do you want to see?</p>
                  <div className="selector-track" role="tablist" aria-label={`${brandName} product videos`}>
                    {visibleProducts.map((id) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={product === id}
                        className={product === id ? 'active' : ''}
                        onClick={() => {
                          setProduct(id);
                          setChapter(loksyVideos[id].chapters[0]);
                        }}
                      >
                        {loksyVideos[id].shortTitle}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {loksyApps ? (
          <section id="citizen" className="loksy-section loksy-cream">
            <div className="loksy-section-inner split">
              <div className="loksy-block-heading">
                <p className="loksy-kicker">01 Citizen</p>
                <h2 className="loksy-h2">
                  <span className="loksy-h2-brand">
                    Loksy
                    <img src="/images/brand/loksy-mark.png" alt="" className="loksy-inline-mark" />
                  </span>
                  <br />
                  Local Services for You.
                </h2>
              </div>
              <div className="loksy-block-copy">
                <p className="loksy-copy">Discover nearby shops, services, deals, jobs and local listings.</p>
                <p className="loksy-copy">
                  <Link to="/loksyapp">Walk through the Loksy app screens →</Link>
                </p>
                <div className="loksy-chapter-note loksy-chapter-quote" key={citizenChapter.title}>
                  <p className="loksy-kicker">{citizenChapter.title}</p>
                  <p>{citizenChapter.description}</p>
                </div>
                <ul className="loksy-feature-list desktop-hide">
                  {CITIZEN_LABELS.map(([n, title]) => (
                    <li key={n}>
                      <span>{n}</span>
                      {title}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="loksy-block-links loksy-cta-row">
                <PlayStoreBadge href={loksyVideos.citizen.playStore} name={loksyVideos.citizen.title} />
                <WebsiteLink to={loksyVideos.citizen.webTo} tone="light">
                  {loksyVideos.citizen.webLabel}
                </WebsiteLink>
              </div>
              <div className="loksy-block-phone">
                <PhoneMockup
                  video={loksyVideos.citizen}
                  size="chapter"
                  controlsTone="light"
                  onChapterChange={setCitizenChapter}
                />
              </div>
            </div>
          </section>
        ) : null}

        {loksyApps ? (
          <section id="mitra" className="loksy-section loksy-night">
            <div className="loksy-section-inner split-rev">
              <div className="loksy-block-heading">
                <p className="loksy-kicker">02 Vyapar</p>
                <h2 className="loksy-h2">
                  One login.
                  <br />
                  Run the shop.
                  <br />
                  Grow the shop.
                </h2>
              </div>
              <div className="loksy-block-copy">
                <p className="loksy-copy">Operate the counter and grow your business from one phone.</p>
                <p className="loksy-copy">
                  <Link to="/loksymitra">Walk through the Vyapar screens →</Link>
                </p>
                <div className="loksy-zone" role="tablist" aria-label="Vyapar zones">
                  {['operate', 'grow'].map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={zone === item ? 'active' : ''}
                      onClick={() => setZone(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <ul className="loksy-zone-list">
                  {zoneItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="loksy-block-links loksy-cta-row">
                <PlayStoreBadge href={loksyVideos.mitra.playStore} name={loksyVideos.mitra.title} />
                <WebsiteLink to={loksyVideos.mitra.webTo}>{loksyVideos.mitra.webLabel}</WebsiteLink>
              </div>
              <div className="loksy-block-phone">
                <PhoneMockup video={loksyVideos.mitra} size="chapter" />
              </div>
            </div>
          </section>
        ) : null}

        {lmsApp ? (
          <section id="lms" className="loksy-section loksy-lms">
            <div className="loksy-section-inner split">
              <div className="loksy-block-heading">
                <p className="loksy-kicker">{knlsOnly ? '01 KNLS LMS' : '03 Students LMS'}</p>
                <h2 className="loksy-h2">
                  Learn to Earn
                  <br />
                  (To Build your Career)
                </h2>
              </div>
              <div className="loksy-block-copy">
                <p className="loksy-copy">Courses, reading, flashcards, typing and progress — on the phone.</p>
                <p className="loksy-display" style={{ marginTop: 32, fontSize: 28 }}>
                  Learn to earn.
                </p>
                <p className="loksy-copy">Courses, reading, flashcards and typing.</p>
                <div className="loksy-chapter-note loksy-chapter-quote" key={lmsChapter.title}>
                  <p className="loksy-kicker">{lmsChapter.title}</p>
                  <p>{lmsChapter.description}</p>
                </div>
              </div>
              <div className="loksy-block-links loksy-cta-row">
                <PlayStoreBadge href={loksyVideos.lms.playStore} name={loksyVideos.lms.title} />
                <WebsiteLink to={loksyVideos.lms.webTo} tone="light">
                  {loksyVideos.lms.webLabel}
                </WebsiteLink>
              </div>
              <div className="loksy-block-phone">
                <PhoneMockup
                  video={loksyVideos.lms}
                  size="chapter"
                  glow="indigo"
                  controlsTone="light"
                  onChapterChange={setLmsChapter}
                />
              </div>
            </div>
          </section>
        ) : null}

        {loksyApps ? (
          <section id="loop" className="loksy-section loksy-loop">
            <p className="loksy-kicker">The Loop</p>
            <h2 className="loksy-h2">
              {lmsApp ? 'Four faces.' : 'Three faces.'}
              <br />
              One town.
            </h2>
            <ol>
              {loopNodes.map(([title, line], index, list) => (
                <li key={title}>
                  <h3>{title}</h3>
                  <p>{line}</p>
                  {index < list.length - 1 ? <div className="loksy-loop-line" /> : null}
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {audience.length > 0 ? (
          <section className="loksy-section loksy-audience">
            <div className="loksy-section-inner" style={{ maxWidth: 900 }}>
              <p className="loksy-kicker">Who it is for</p>
              {audience.map(([who, line, app, href]) => (
                <a key={who} href={href}>
                  <div>
                    <h3>{who}</h3>
                    <p>{line}</p>
                  </div>
                  <span>{app}</span>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        <section id="film" className="loksy-section loksy-film">
          {knlsOnly ? (
            <h2 className="loksy-h2">
              Learn to Earn.
              <br />
              <span style={{ color: '#6366f1' }}>KNLS LMS.</span>
            </h2>
          ) : (
            <h2 className="loksy-h2">
              Your Town.
              <br />
              Just One Click Away.
              <br />
              <span style={{ color: '#ff6b00' }}>Loksy.</span>
            </h2>
          )}
          <div style={{ marginTop: 56, display: 'flex', justifyContent: 'center' }}>
            <PhoneMockup
              video={knlsOnly ? loksyVideos.lms : loksyApps && !lmsApp ? loksyVideos.citizen : loksyVideos.overview}
              size="film"
              glow={knlsOnly ? 'indigo' : 'orange'}
            />
          </div>
        </section>

        <section id="download" className="loksy-section loksy-download">
          <h2 className="loksy-h2">
            {knlsOnly ? 'Ready to start learning?' : 'Ready to explore Loksy?'}
          </h2>
          <div className="loksy-cards">
            {downloadApps.map((app) => (
              <article key={app.id} className="loksy-card">
                <small>{knlsOnly && app.id === 'lms' ? 'KNLS LMS' : app.title}</small>
                <h3>{app.tagline}</h3>
                <div className="loksy-card-actions">
                  <PlayStoreBadge href={app.playStore} name={app.title} />
                  <WebsiteLink to={app.webTo}>{app.webLabel}</WebsiteLink>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="loksy-footer">
        <div className="loksy-footer-grid">
          <div>
            <p className="loksy-brand" style={{ color: '#fff' }}>
              {brandName}
            </p>
            <p>{knlsOnly ? 'Learn to Earn. Build your career.' : 'Your Town. Just One Click Away.'}</p>
          </div>
          <div>
            <h4>Products</h4>
            <ul>
              {loksyApps ? (
                <>
                  <li><Link to="/loksyapp">Loksy App</Link></li>
                  <li><Link to="/loksymitra">Loksy Vyapar</Link></li>
                </>
              ) : null}
              {lmsApp ? (
                <li><a href="#lms">{knlsOnly ? 'KNLS LMS' : 'Students LMS'}</a></li>
              ) : null}
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li><a href="#home">About</a></li>
              <li><Link to="/privacy-policy">Privacy</Link></li>
              <li>
                <Link to={knlsOnly ? '/terms-and-conditions' : '/locsy-terms-and-conditions'}>
                  Terms
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Support</h4>
            <ul>
              <li><Link to="/enquiryform">Enquiry form</Link></li>
              <li><Link to={knlsOnly ? '/jobalerts' : '/job-alerts'}>Job Alerts</Link></li>
              {knlsOnly ? <li><Link to="/training-programs">Courses</Link></li> : null}
              <li><a href="#download">Help</a></li>
            </ul>
          </div>
        </div>
        <p className="loksy-copy-year">© 2026 {brandName}</p>
      </footer>
    </div>
  );
}
